import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { promoAPI } from '@/lib/api';
import { Heart, Download, Play, Pause, Music } from 'lucide-react';

export default function PromoListen() {
  const { token } = useParams();
  const [track, setTrack] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // Player state
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playFired, setPlayFired] = useState(false);

  // Engagement state
  const [liked, setLiked] = useState(false);
  const [downloaded, setDownloaded] = useState(false);

  useEffect(() => {
    promoAPI.getListenData(token)
      .then(r => {
        setTrack(r.data);
        setLiked(r.data.liked || false);
        setDownloaded(r.data.downloaded || false);
        setPlayFired(r.data.played || false);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [token]);

  // Wire audio events
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onTimeUpdate = () => setCurrentTime(audio.currentTime);
    const onDurationChange = () => setDuration(audio.duration || 0);
    const onEnded = () => setPlaying(false);

    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('durationchange', onDurationChange);
    audio.addEventListener('ended', onEnded);
    return () => {
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('durationchange', onDurationChange);
      audio.removeEventListener('ended', onEnded);
    };
  }, [track]);

  const togglePlay = async () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      audio.pause();
      setPlaying(false);
    } else {
      await audio.play();
      setPlaying(true);
      if (!playFired) {
        setPlayFired(true);
        promoAPI.logEvent(token, 'play').catch(() => {});
      }
    }
  };

  const handleLike = async () => {
    if (liked) return;
    setLiked(true);
    promoAPI.logEvent(token, 'like').catch(() => {});
  };

  const handleDownload = async () => {
    if (!track) return;
    setDownloaded(true);
    promoAPI.logEvent(token, 'download').catch(() => {});
    // Trigger download
    const a = document.createElement('a');
    a.href = track.wav_url;
    a.download = `${track.track_title} - ${track.artist}.wav`;
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const fmt = (s) => {
    if (!s || isNaN(s)) return '0:00';
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60).toString().padStart(2, '0');
    return `${m}:${sec}`;
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  const handleSeek = (e) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    audio.currentTime = ratio * duration;
  };

  // ── Loading ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0D0D12] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#A62B1F] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // ── Not found ─────────────────────────────────────────────────────────────
  if (notFound || !track) {
    return (
      <div className="min-h-screen bg-[#0D0D12] flex items-center justify-center text-center px-6">
        <div>
          <Music className="w-12 h-12 text-[#5C6670] mx-auto mb-4" />
          <p className="text-white text-lg font-semibold mb-2">Link not found</p>
          <p className="text-[#5C6670] text-sm">This promo link may have expired or is invalid.</p>
        </div>
      </div>
    );
  }

  // ── Listen page ───────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#0D0D12] flex flex-col items-center justify-center px-6 py-12"
         style={{ fontFamily: 'Manrope, Arial, sans-serif' }}>

      {/* Hidden audio element */}
      {track.stream_url && (
        <audio ref={audioRef} src={track.stream_url} preload="metadata" />
      )}

      <div className="w-full max-w-sm">

        {/* Artwork */}
        <div className="relative mb-8">
          {track.artwork_url ? (
            <img
              src={track.artwork_url}
              alt="Artwork"
              className="w-full aspect-square rounded-2xl object-cover shadow-2xl"
              style={{ boxShadow: '0 24px 64px rgba(166,43,31,0.3)' }}
            />
          ) : (
            <div
              className="w-full aspect-square rounded-2xl flex items-center justify-center"
              style={{
                background: 'linear-gradient(135deg, #A62B1F 0%, #A62B1F 100%)',
                boxShadow: '0 24px 64px rgba(166,43,31,0.3)',
              }}
            >
              <Music className="w-24 h-24 text-white opacity-80" />
            </div>
          )}
        </div>

        {/* Track info */}
        <div className="mb-6 text-center">
          <p className="text-xs text-[#A62B1F] font-bold uppercase tracking-widest mb-1">
            New Release
          </p>
          <h1 className="text-2xl font-extrabold text-white leading-tight mb-1">
            {track.track_title}
          </h1>
          <p className="text-[#9AA3AB] text-base">{track.artist}</p>
        </div>

        {/* Personal message */}
        {track.message && (
          <div className="mb-6 px-4 py-3 rounded-xl bg-white/5 border border-white/10">
            <p className="text-[#CFC8BB] text-sm leading-relaxed italic">
              "{track.message}"
            </p>
            {track.recipient_name && (
              <p className="text-[#5C6670] text-xs mt-2">— sent to {track.recipient_name}</p>
            )}
          </div>
        )}

        {/* Progress bar */}
        <div className="mb-4">
          <div
            className="w-full h-1.5 bg-white/10 rounded-full cursor-pointer relative overflow-hidden"
            onClick={handleSeek}
          >
            <div
              className="h-full rounded-full transition-all"
              style={{
                width: `${progress}%`,
                background: 'linear-gradient(90deg, #A62B1F, #A62B1F)',
              }}
            />
          </div>
          <div className="flex justify-between mt-1">
            <span className="text-xs text-[#5C6670]">{fmt(currentTime)}</span>
            <span className="text-xs text-[#5C6670]">{fmt(duration)}</span>
          </div>
        </div>

        {/* Play button */}
        <div className="flex items-center justify-center mb-8">
          <button
            onClick={togglePlay}
            className="w-16 h-16 rounded-full flex items-center justify-center transition-transform hover:scale-105 active:scale-95"
            style={{ background: 'linear-gradient(135deg, #A62B1F, #A62B1F)' }}
          >
            {playing ? (
              <Pause className="w-7 h-7 text-white" />
            ) : (
              <Play className="w-7 h-7 text-white ml-1" />
            )}
          </button>
        </div>

        {/* Like & Download */}
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={handleLike}
            className={`flex items-center justify-center gap-2 py-3.5 rounded-xl font-semibold text-sm transition-all ${
              liked
                ? 'bg-[#A62B1F] text-white'
                : 'bg-white/8 text-[#9AA3AB] hover:bg-white/12 border border-white/10'
            }`}
          >
            <Heart className={`w-4 h-4 ${liked ? 'fill-white' : ''}`} />
            {liked ? 'Liked!' : 'I Like This'}
          </button>

          <button
            onClick={handleDownload}
            className={`flex items-center justify-center gap-2 py-3.5 rounded-xl font-semibold text-sm transition-all ${
              downloaded
                ? 'bg-[#A62B1F] text-white'
                : 'bg-white/8 text-[#9AA3AB] hover:bg-white/12 border border-white/10'
            }`}
          >
            <Download className="w-4 h-4" />
            {downloaded ? 'Downloaded' : 'Download WAV'}
          </button>
        </div>

        {/* Branding */}
        <p className="text-center text-[#3D3D4A] text-xs mt-10">
          Sent via <span className="text-[#5C6670]">Stagehead OS</span> · Anyone Music Records
        </p>
      </div>
    </div>
  );
}
